import { FilterList as FilterListIcon, PlusOne, SearchOff } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import {
  Badge,
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { EquipmentFormType } from "../../../types/forms/warehouse/equipment";
import { EquipmentResponse } from "../../../types/model/warehouse/equipment";
import GenericFilter from "../../General/GenericFilter";
import EquipmentRow from "./EquipmentRow";
import NewEquipmentPanel from "./NewEquipmentPanel";
import { useEquipmentFilters } from "../../../hooks/Warehouse";
import { FilterOptions } from "../../../hooks/Warehouse/useEquipmentFilters";

interface WarehouseEquipmentTableProps {
  equipmentData: EquipmentResponse[];
  canEdit: boolean;
  onCreate: (values: EquipmentFormType, images: File[]) => Promise<void>;
  onUpdate: (equipmentId: number, values: EquipmentFormType) => Promise<void>;
  onDelete: (equipmentId: number) => Promise<void>;
  onRequestRefresh?: () => void;
}

const NEW_FORM_ID = "form-new";

export default function WarehouseEquipmentTable({
  equipmentData,
  canEdit,
  onCreate,
  onUpdate,
  onDelete,
  onRequestRefresh,
}: Readonly<WarehouseEquipmentTableProps>) {
  const { t } = useTranslation();
  const accountId = useSelector((state: RootState) => state.session.accountId);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openRowId, setOpenRowId] = useState<number | null | string>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const {
    draftFilters,
    setDraftFilters,
    filterFields,
    activeFilters,
    filteredData,
    applyFilters,
    clearFilters,
    removeFilter,
  } = useEquipmentFilters(equipmentData, () => setPage(0));

  const columnCount = canEdit ? 7 : 6;
  const isNewOpen = openRowId === "new";
  const isFilterOpen = Boolean(filterAnchorEl);
  const visibleRows = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleToggleFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(filterAnchorEl ? null : event.currentTarget);
  };

  const handleCloseFilter = () => setFilterAnchorEl(null);

  const handleClearFilters = () => {
    clearFilters();
    handleCloseFilter();
  };

  const handleCreate = async (values: EquipmentFormType, images: File[]) => {
    try {
      setIsCreating(true);
      await onCreate(values, images);
      setOpenRowId(null);
    } catch {
      // Error already handled by parent, keep the panel open
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h5">
          {t("warehouse.tabs.equipment")}
        </Typography>

        <Tooltip title={t("generic.filter")}>
          <IconButton onClick={handleToggleFilter}>
            <Badge badgeContent={activeFilters.length} color="primary">
              <FilterListIcon color={isFilterOpen ? "primary" : undefined} />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title={t("generic.refresh")}>
          <IconButton onClick={() => onRequestRefresh?.()}>
            <CachedIcon />
          </IconButton>
        </Tooltip>

        {canEdit && (
          <Tooltip title={t("warehouse.equipment.add-new")}>
            <IconButton onClick={() => setOpenRowId(isNewOpen ? null : "new")}>
              <PlusOne />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>

      {activeFilters.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
          {activeFilters.map((filter) => (
            <Chip
              key={filter.key}
              label={`${filter.label}: ${filter.value}`}
              onDelete={() => removeFilter(filter.key)}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      )}

      <Popover
        open={isFilterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { p: 3, width: 350 } } }}
      >
        <GenericFilter
          fields={filterFields}
          filters={draftFilters}
          onFiltersChange={(newFilters) => setDraftFilters(newFilters as FilterOptions)}
          onApplyFilters={(applied) => applyFilters((applied as FilterOptions) ?? draftFilters)}
          onClearFilters={handleClearFilters}
        />
      </Popover>

      <TableContainer>
        <Table aria-label="equipment table">
          {!isNewOpen && (
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "5%" }} />
                <TableCell>{t("warehouse.equipment.equipment")}</TableCell>
                <TableCell>{t("warehouse.equipment.serial-number")}</TableCell>
                <TableCell>{t("warehouse.equipment.location")}</TableCell>
                <TableCell>{t("warehouse.equipment.status")}</TableCell>
                <TableCell>{t("warehouse.equipment.maintenance")}</TableCell>
                {canEdit && (
                  <TableCell sx={{ width: "10%" }} align="right">
                    {t("generic.actions")}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {isNewOpen && (
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columnCount}>
                  <Collapse in timeout="auto" unmountOnExit>
                    <NewEquipmentPanel
                      formId={NEW_FORM_ID}
                      isCreating={isCreating}
                      accountId={accountId}
                      onCancel={() => setOpenRowId(null)}
                      onSubmit={handleCreate}
                    />
                  </Collapse>
                </TableCell>
              </TableRow>
            )}

            {visibleRows.length === 0 && !isNewOpen && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 10 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", opacity: 0.6 }}>
                    <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                    <Typography variant="h6">{t("generic.no-records")}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {visibleRows.map((row) => (
              <EquipmentRow
                key={row.id}
                row={row}
                open={openRowId === row.id}
                canEdit={canEdit}
                columnCount={columnCount}
                onToggle={() => setOpenRowId(openRowId === row.id ? null : row.id)}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number.parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  );
}